import { PageHeader } from '@ant-design/pro-layout';
import { Select, Spin, Tag } from 'antd';
import { useDestination } from 'api/destinations.api';
import {
    DestinationGeoType,
    parseGeoType,
} from 'constants/destination-geo-type';
import React from 'react';
import {
    useNavigate,
    useParams,
    useLocation,
    useSearchParams,
} from 'react-router-dom';
import { DestinationEditor } from '../../components/DestinationEditor';
import DestinationEditorProvider, {
    useDestinationEditor,
} from '../../components/DestinationEditor/DestinationEditorContext';

import styles from './DestinationEditorPage.module.css';

export function DestinationEditorPage() {
    const { id: destinationId } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const { destination, isLoading } = useDestination(destinationId || '');

    // Parse geo_type from URL for new destinations, default to PLACE
    const geoTypeParam = searchParams.get('geo_type');
    const initialGeoType = parseGeoType(geoTypeParam);

    if (isLoading) {
        return (
            <div className="full-page-content-center column-center">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <DestinationEditorProvider
            destination={destination}
            initialGeoType={initialGeoType}
        >
            <DestinationEditorPageInner
                destinationId={destinationId || ''}
                isLoading={isLoading}
            />
        </DestinationEditorProvider>
    );
}

type DestinationEditorPageInnerProps = Readonly<{
    destinationId: string;
    isLoading: boolean;
}>;

function DestinationEditorPageInner({
    destinationId,
}: DestinationEditorPageInnerProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { locale, setLocale, destination } = useDestinationEditor();

    const isCountry = destination.geo_type === DestinationGeoType.COUNTRY;

    const handleBack = () => {
        // Check if we have a returnTo state from the Link navigation
        const returnTo = location.state?.returnTo;

        console.log('returnTo', returnTo);

        if (returnTo) {
            // Navigate to the exact URL with preserved parameters
            navigate(returnTo);
        } else {
            // Fallback to destinations overview
            navigate('/destinations/overview');
        }
    };

    return (
        <div className="full-page">
            <PageHeader
                title={
                    <div className={styles.title}>
                        {destinationId
                            ? 'Edit destination'
                            : 'Add new destination'}
                        <Tag color={isCountry ? 'green' : 'blue'}>
                            {isCountry ? 'Country' : 'Place'}
                        </Tag>
                    </div>
                }
                ghost={false}
                onBack={handleBack}
                extra={
                    <Select
                        className={styles.localeSelector}
                        size="large"
                        value={locale}
                        placeholder="Select a person"
                        onChange={setLocale}
                        options={[
                            {
                                value: 'en',
                                label: '🇬🇧 English',
                            },
                            {
                                value: 'nl',
                                label: '🇳🇱 Dutch',
                            },
                            {
                                value: 'fr',
                                label: '🇫🇷 French',
                            },
                            {
                                value: 'de',
                                label: '🇩🇪 German',
                            },
                            {
                                value: 'pt',
                                label: '🇵🇹 Portuguese',
                            },
                            {
                                value: 'es',
                                label: '🇪🇸 Spanish',
                            },
                        ]}
                    />
                }
            />
            <DestinationEditor />
        </div>
    );
}
